import { useLocale, useTranslations } from 'next-intl';
import React from 'react';

import { MovieDto } from '@cineamo/api/models/movie/v2/MovieDto.types';

import { WEBAPP_URL } from '@cineamo/api/constants/http-constants';
import { IMDB_MOVIE_URL } from '@cineamo/api/constants/imdb-constants';
import { TMDB_MOVIE_URL, TMDB_SERIES_URL } from '@cineamo/api/constants/tmdb-constants';

import { loadAsset } from '@cineamo/ui/src/helpers/asset-helper';
import { cn } from '@cineamo/ui/src/helpers/cn';

import ImageCineamoText from '@cineamo/ui/src/icons/app-icon/im-cineamoText';

import MoviePartner from './MoviePartner';

const moviePilotLogo = loadAsset('/images/companies/partners/im-moviePilotLogoFlat.svg');
const tmdbLogo = loadAsset('/images/companies/partners/im-tmdbShort.svg');

export enum MoviePartnerBoxDirection {
    Horizontal,
    Vertical
}

/**
 * Pure presentational component for displaying partner logos and links.
 * Shows distributor, TMDB, IMDB, and MoviePilot links with logos.
 * Supports horizontal or vertical orientation.
 */
const MoviePartnerBox: React.FC<{
    className?: string;
    movieSlug?: string;
    movieTmdbId: MovieDto['tmdbId'];
    movieImdbId: MovieDto['imdbId'];
    movieThirdParties: MovieDto['thirdParties'];
    movieSeries: MovieDto['series'];
    distributor?: {
        name?: string;
        logoUrl?: string;
        homepageUrl?: string;
    };
    aboutMovie?: 'Cineamo' | 'IMDB';
    desktopOrientation?: MoviePartnerBoxDirection;
}> = ({
    className,
    movieSlug,
    movieTmdbId,
    movieImdbId,
    movieThirdParties,
    movieSeries,
    distributor,
    aboutMovie = 'IMDB',
    desktopOrientation = MoviePartnerBoxDirection.Horizontal
}) => {
    const t = useTranslations();
    const locale = useLocale();

    const tmdbId = movieTmdbId;
    const imdbId = movieImdbId;
    const thirdParties = movieThirdParties;
    const aboutMovieUrl =
        aboutMovie === 'IMDB' ? IMDB_MOVIE_URL(imdbId ?? '') : `${WEBAPP_URL}/${locale}/movie/${movieSlug}`;

    return (
        <div
            className={cn(
                className ??
                    (desktopOrientation === MoviePartnerBoxDirection.Horizontal ? 'gap-x-48 gap-y-24' : 'gap-32'),
                desktopOrientation === MoviePartnerBoxDirection.Horizontal
                    ? 'flex flex-row flex-wrap items-center justify-center lg:justify-start'
                    : 'flex flex-col'
            )}>
            {distributor?.logoUrl && (
                <MoviePartner
                    title={t('movie.a-movie-by')}
                    url={distributor.homepageUrl ?? ''}
                    logo={distributor.logoUrl}
                    logoAlt={distributor?.name || 'Distributor Logo'}
                />
            )}
            {tmdbId && (
                <MoviePartner
                    title={t('movie.data-provided-by')}
                    url={movieSeries === true ? TMDB_SERIES_URL(tmdbId) : TMDB_MOVIE_URL(tmdbId)}
                    logo={tmdbLogo}
                    logoAlt="TMDB"
                />
            )}
            {aboutMovie === 'Cineamo' && aboutMovieUrl && (
                <MoviePartner
                    title={t('movie.about-movie')}
                    url={aboutMovieUrl}
                    customImage={<ImageCineamoText className="w-auto h-32" />}
                    logoAlt="Cineamo"
                />
            )}
            {thirdParties?.moviePilot?.link && (
                <MoviePartner
                    title={t('movie.discussed-on')}
                    url={thirdParties?.moviePilot?.link}
                    logo={moviePilotLogo}
                    logoAlt="MoviePilot"
                />
            )}
        </div>
    );
};

export default MoviePartnerBox;
